import React, { useEffect } from 'react';
import { useStoreState, useStoreActions } from 'easy-peasy';
import { useTheme } from '@mui/material/styles';
import { Slide, Box, Typography, Stack, IconButton, Tooltip } from '@mui/material';
import {
	Close,
	CheckCircleRounded,
	Print,
	Language,
	CorporateFare,
	CalendarMonth,
	ViewColumn
} from '@mui/icons-material';
import utils from 'components/utils';
import moment from 'moment';

const KitItemDetail = props => {
	const { ean, open, onClose } = props;
	const theme = useTheme();
	const translate = utils.useI18N();
	const kitDetailItems = useStoreState(state => state.kitUsers.kitDetailItems);
	const detailEan = useStoreState(state => state.kitUsers.detailEan);
	const book = kitDetailItems.filter(item => item.ean === detailEan)[0];

	const availabilityMap = {
		POOR: theme.palette.custom.red,
		FAIR: theme.palette.custom.yellow,
		GOOD: theme.palette.custom.greenlight,
		EXCELLENT: theme.palette.custom.green
	};

	const infoIcons = [
		{ label: 'catalog.details.infoBox.ean', value: book.ean, icon: <ViewColumn /> },
		{ label: 'catalog.details.infoBox.publisher', value: book.publisher, icon: <CorporateFare /> },
		{
			label: 'catalog.details.infoBox.publicationDate',
			value: moment(book.publicationDate).format('L'),
			icon: <CalendarMonth />
		},
		{ label: 'catalog.details.infoBox.language', value: book.language, icon: <Language /> }
	];

	const Transition = React.forwardRef(function Transition(props, ref) {
		return <Slide direction="up" ref={ref} {...props} />;
	});

	return (
		<Stack spacing={1}>
			<Stack
				direction="row"
				justifyContent="space-between"
				alignItems="center"
				spacing={0}
				sx={{
					width: '100%',
					borderWidth: '0 0 1px 0',
					borderStyle: 'solid',
					borderColor: theme.palette.custom.black
				}}
			>
				<Typography variant="titleTab">{translate('kit.bookCard')}</Typography>
				<IconButton onClick={onClose}>
					<Close />
				</IconButton>
			</Stack>
			<Stack
				direction={{ xs: 'column', md: 'row', sm: 'row' }}
				justifyContent="flex-start"
				alignItems="flex-start"
				spacing={{ xs: 1, md: 2, sm: 2 }}
				sx={{ width: '100%', padding: '10px' }}
			>
				<Box
					component="img"
					src={book.coverImageURL}
					alt={book.title}
					sx={{
						display: { xs: 'none', md: 'inherit', sm: 'inherit' },
						width: { xs: '0%', md: '200px', sm: '200px' }
					}}
				/>
				<Stack spacing={0.5} sx={{ width: '100%' }}>
					<Stack
						spacing={0.5}
						sx={{
							borderWidth: '0 0 1px 0',
							borderStyle: 'solid',
							borderColor: theme.palette.custom.greyborder,
							paddingBottom: '10px'
						}}
					>
						<Typography variant="titlePage">{book.title}</Typography>
						<Stack direction="row" spacing={0.5} sx={{ marginTop: '-3px !important' }}>
							<Typography variant="text">{translate('common.of')}</Typography>
							<Typography variant="text" sx={{ fontWeight: '600', textTransform: 'capitalize' }}>
								{book.author}
							</Typography>
						</Stack>
						<Typography variant="text">{book.serie}</Typography>
						<Typography
							variant="text"
							sx={{
								fontWeight: '600',
								color: theme.palette.primary.main,
								'&::first-letter': {
									textTransform: 'uppercase'
								}
							}}
						>
							{book.newsItem}
						</Typography>
						<Stack
							direction="row"
							justifyContent="space-between"
							alignItems="center"
							spacing={0}
							sx={{ width: '100%', marginTop: '10px !important' }}
						>
							{book.availability && (
								<Stack direction="row" spacing={1} sx={{ marginTop: '5px !important' }}>
									{book.availability !== 'PRINT_ON_DEMAND' && (
										<Tooltip
											title={translate(
												'catalog.availabilityDesc' + book.availability.toLowerCase()
											)}
										>
											<CheckCircleRounded
												sx={{ color: availabilityMap[book.availability], fontSize: '20px' }}
											/>
										</Tooltip>
									)}
									{book.availability === 'PRINT_ON_DEMAND' && (
										<Tooltip
											title={translate(
												'catalog.availabilityDesc' + book.availability.toLowerCase()
											)}
										>
											<Print sx={{ fontSize: '20px' }} />
										</Tooltip>
									)}
									<Typography variant="text" sx={{ color: availabilityMap[book.availability] }}>
										{translate('catalog.availability.' + book.availability.toLowerCase())}
									</Typography>
								</Stack>
							)}
							<Stack justifyContent="flex-end" sx={{ width: !book.availability ? '100%' : 'auto' }}>
								<Typography
									variant="text"
									sx={{ fontWeight: '600', fontSize: '20px', textAlign: 'right' }}
								>
									{utils.formatPrice(book.fullPrice)}
								</Typography>
							</Stack>
						</Stack>
					</Stack>
					<Stack
						direction="row"
						justifyContent="flex-start"
						alignItems="center"
						spacing={{ xs: 1, md: 3, sm: 2 }}
						sx={{
							borderWidth: '0 0 1px 0',
							borderStyle: 'solid',
							borderColor: theme.palette.custom.greyborder,
							paddingBottom: '10px'
						}}
					>
						{infoIcons.map((icon, index) => (
							<Stack
								key={index}
								direction="column"
								justifyContent="center"
								alignItems="center"
								spacing={0}
								sx={{}}
							>
								<Typography variant="text" sx={{ fontSize: '13px' }}>
									{translate(icon.label)}
								</Typography>
								{icon.icon}
								<Typography variant="text" sx={{ fontSize: '13px', fontWeight: '600' }}>
									{icon.value}
								</Typography>
							</Stack>
						))}
					</Stack>
					<Stack>
						<Typography variant="text" sx={{ fontSize: '14px' }}>
							{book.synopsis}
						</Typography>
					</Stack>
				</Stack>
			</Stack>
		</Stack>
	);
};

export default KitItemDetail;
